import * as React from 'react';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

/**
 * Coarse-pointer tap target (44px), grown rather than reserved — see `select.tsx`
 * for the mechanism and why it lives in the primitive.
 *
 * BOTH axes, unlike the full-width form fields: a toggle is usually icon-only
 * (`min-w-10` — a 40x40 editor-toolbar button), so flooring only the height would
 * leave a target that is tall enough and still too narrow. Straight `min-h`/`min-w`
 * rather than the `Switch`/`Checkbox` reserve-then-fill overlay, for the reason
 * `Button size="icon"` gives: an overlay bleeds out of a `py-1` `DataTable` cell
 * into the neighbouring row.
 *
 * On the base string, not per size, so it also covers `lg` (already `h-11`) and any
 * size added later — and it reaches `ToggleGroupItem`, which composes these
 * variants. Both are pinned by `__tests__/touchTargetFloor.test.tsx`.
 */
const TOGGLE_TOUCH_TARGET = 'touch:min-h-[44px] touch:min-w-[44px]';

/**
 * The selected ("on") rendering — a FILL plus a SHAPE signal, per
 * `.claude/rules/STYLE_GUIDE.md` § Keyboard Navigation: "a solid fill alone is
 * too weak next to the hover state. Use `bg-accent` plus `ring-2 ring-inset
 * ring-primary`."
 *
 * The fill alone was not weak here, it was NOTHING: `bg-accent` is also the
 * outline variant's hover fill, so selected and hovered resolved to the same
 * declaration and a chosen option measured 1.17:1 against the card in dark mode
 * (1.09:1 in light) — every rating scale in the app read as unrated, reported on
 * the People review form (WI 2f12a39b). A fill can collide with a hover fill; a
 * ring the hover state does not draw cannot.
 *
 * `ring-inset`, not an outward ring: these sit gap-1 apart inside a ToggleGroup
 * and inside `p-0.5` segmented tracks, both of which an outward 2px ring bleeds
 * over.
 *
 * The `data-[state=on]:focus-visible:` layer is not decoration. `data-[state=on]:`
 * and `focus-visible:` compile to selectors of EQUAL specificity — `[data-state]`
 * and `:focus-visible` are both (0,2,0) with the class — and Tailwind emits the
 * `data-*` layer later, so a bare `data-[state=on]:ring-primary` silently wins
 * over `focus-visible:ring-ring` and a selected, keyboard-focused toggle shows
 * exactly the ring it shows at rest. Restating the focus ring under BOTH variants
 * (0,3,0) puts it back on top; `ring-offset-0` goes with it because the offset
 * shadow paints an opaque band over the inset ring's outer edge, which would hide
 * the selected state for as long as focus is on it.
 *
 * Pinned by `__tests__/toggleSelectedState.test.tsx`, from the rendered classes —
 * nothing else in the pipeline can see a colour.
 */
const TOGGLE_SELECTED =
  'data-[state=on]:bg-accent data-[state=on]:text-accent-foreground ' +
  'data-[state=on]:ring-2 data-[state=on]:ring-inset data-[state=on]:ring-primary ' +
  'data-[state=on]:focus-visible:ring-4 data-[state=on]:focus-visible:ring-offset-0 ' +
  'data-[state=on]:focus-visible:ring-ring';

const toggleVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 gap-2 ' +
    TOGGLE_SELECTED +
    ' ' +
    TOGGLE_TOUCH_TARGET,
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-input bg-transparent hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'h-10 px-3 min-w-10',
        sm: 'h-9 px-2.5 min-w-9',
        lg: 'h-11 px-5 min-w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle, toggleVariants };
